import React, { useEffect, useMemo, useRef, useState } from 'react';
import MonsterSelectDesktop from './Desktop/MonsterSelect';
import MonsterSelectMobile from './Mobile/MonsterSelect';
import MonsterImage from '../components/MonsterImage';
import { getMonsterImgPath } from '../utils/monster';
import { Replay as ReplayData, BattleLog, Element, SET } from './simTypes';
import { SimUnit, Stats, Slot, BattleField, nameMap, skillIdOf, skillIconUrl, skillLabel, slotIndexOf, floatsOf, usedSkillsOf, strikesOf, passivesOf, CritIcon, noteLabel, preloadSkillIcons } from './simShared';
import SimManual from './SimManual';
import { fetchDefenseRecords, justTrainedRecord } from '../utils/simRecords';
import SimMemory, { MemoryRecord, MemoryDeck, DeckVerdict, VerdictBadge, verdictText, isOverwhelming } from './SimMemory';
import SimTrain from './SimTrain';
import SimFitRunes, { SimFitRunesHandle } from './SimFitRunes';
import SimRuneTune, { SimRuneTuneHandle } from './SimRuneTune';
import { BuildEditor, BuildRunes, TacticBuild, ArtPair, artsOfRaw, splitArt, mergeArts, buildFromReplayUnit, hasBuild, setsText } from './TacticBuild';
import { rawOf } from './BattleRunes';
import { useBackHandler } from '../hooks/useBackHandler';
import { guildImg } from '../utils/guildImage';
import { takeSimBattle, takeSimLearned } from '../utils/simHandoff';
import ProfileWithName from '../components/ProfileWithName';
import { getTheme } from '../utils/theme';

/**
 * **전투 판 색** — 밝은 화면·다크모드를 따로 정한다 (사용자 2026-10-27).
 *   다크: 남색 판 + 보라 테두리 한 줄 (전투 판 초안 2번)
 *   밝은: 연보라 판 + 보라 테두리 한 줄 (밝은 화면 초안 1번 — 다크 판을 밝게 한 짝)
 * 빈 몹 칸은 둘 다 **점선**(사용자 "다크모드에서도 몹박스에 점선"), 몹이 들면 실선.
 * 다크모드 칠하기(utils/theme.ts)는 밝은 색만 어둡게 바꾸므로, 다크 쪽은 처음부터 어두운 색으로 적어 칠하기가 손대지 않게 한다.
 */
function boxColors(dark: boolean) {
    return dark ? {
        panel: { background: '#30355a', boxShadow: 'inset 0 0 0 2px #7b6fe0, 0 6px 20px rgba(30,34,70,0.25)' },
        leader: '#b9bdf0', leaderVal: '#a9b0ff',
        firstBtn: { border: 'none', background: 'rgba(102,126,234,0.2)', color: '#c9ccf5' },
        slotBg: '#262a48', slotEmptyBorder: '#5a5fa8', slotBorder: '#4a4f80', slotSel: '#c9ccf5',
        plus: '#9aa0e8', leaderMark: '#c9ccf5', name: '#e6e8ff', stat: '#b9c6ff', statMuted: '#9aa0c8', sets: '#9aa6dd'
    } : {
        panel: { background: '#f1f2ff', boxShadow: 'inset 0 0 0 2px #8b7ff0, 0 4px 16px rgba(102,126,234,0.10)' },
        leader: '#5a5fd6', leaderVal: '#764ba2',
        firstBtn: { border: 'none', background: 'rgba(102,126,234,0.12)', color: '#5a5fd6' },
        slotBg: '#fff', slotEmptyBorder: '#b3b7ee', slotBorder: '#c9ccf5', slotSel: '#667eea',
        plus: '#8b8fe0', leaderMark: '#5a5fd6', name: '#444', stat: '#5a6acf', statMuted: '#888', sets: '#7d82b8'
    };
}

/**
 * **시뮬레이션 탭** — 공덱·방덱을 고르면 swSim 엔진(`swSim/engine`, 서버가 HTTP 로 부른다 — `backend/src/sim/swsim.ts`)으로 싸워 본다.
 * 엔진은 **서버에서만** 돈다(사용자 지시 2026-10-11: 브라우저에서 돌면 엔진 코드를 가져갈 수 있다) — 화면은 칸 입력을 보내고 결과만 받는다.
 *
 * 몹은 전적검색과 같은 고르기 창·이미지를 쓴다(사용자 지시 2026-10-10). 능력치는 **실전 기록의 중앙값**(몹 유형별,
 * 맨몸 대비)으로 채우고 사람이 고칠 수 있다. 승률은 여러 번 돌린 값이고, 아래에 한 판을 그대로 재생한다.
 *
 * 엔진은 아직 실전과 다르다(실전 260판: 실제 공덱 승률 91%, 실제보다 싸움이 길다) — 화면엔 실측 승률만 적어 둔다.
 */

/**
 * **실전 기록에서 뽑은 기본 능력치** — 실전 246판 몹 1,476마리, 몹 유형별 중앙값(2026-10-10).
 * 체력·공격·방어는 맨몸의 몇 배, 속도는 맨몸에 더하는 값, 나머지는 그대로의 값.
 */
const TYPICAL: Record<string, { hp: number; atk: number; def: number; spd: number; cr: number; cd: number; res: number; acc: number }> = {
    Attack:  { hp: 2.54, atk: 2.78, def: 1.65, spd: 167, cr: 71, cd: 135, res: 19, acc: 24 },
    HP:      { hp: 3.58, atk: 1.74, def: 2.03, spd: 153, cr: 33, cd: 61, res: 29, acc: 28 },
    Defense: { hp: 3.51, atk: 1.53, def: 2.73, spd: 151, cr: 26, cd: 57, res: 33, acc: 24 },
    Support: { hp: 3.91, atk: 1.55, def: 2.09, spd: 166, cr: 25, cd: 55, res: 45, acc: 24 }
};

function typicalStats(u: SimUnit): Stats {
    const b = u.base!;
    const t = TYPICAL[u.archetype || ''] || TYPICAL.Support;
    return {
        hp: Math.round(b.hp * t.hp), atk: Math.round(b.atk * t.atk), def: Math.round(b.def * t.def),
        spd: Math.round(b.spd + t.spd), critRate: t.cr, critDamage: t.cd, resistance: t.res, accuracy: t.acc
    };
}

const DEFAULT_SETS = [SET.폭주, SET.의지];


const RUNS = 1000;
const emptySlot = (): Slot => ({ pick: null, unit: null, stats: null, sets: DEFAULT_SETS, build: null, art: {} });

/** 기본 능력치(실전 중앙값)를 룬 설정 모양으로 — 룬 설정 창을 열 때 여기서 시작한다 */
function typicalBuild(s: Slot): TacticBuild | null {
    if (!s.stats) return null;
    const t = s.stats;
    return { sets: [...s.sets], hp: t.hp, atk: t.atk, def: t.def, spd: t.spd, cr: t.critRate, cd: t.critDamage, res: t.resistance, acc: t.accuracy };
}

/** 아티팩트 부옵 합 — 리플레이 원본의 `sec_effects` [효과 번호, 값, …] (엔진이 추가 피해·치피 등에 쓴다) */
function artOf(raw: any): Record<number, number> {
    const art: Record<number, number> = {};
    for (const a of raw?.artifacts || []) for (const e of a?.sec_effects || []) {
        const id = Number(e?.[0]), v = Number(e?.[1]);
        if (id && Number.isFinite(v)) art[id] = (art[id] || 0) + v;
    }
    return art;
}

/** 서버에 보낼 칸 하나 — 몹 번호·능력치(룬 설정, 없으면 기본 능력치)·세트·아티팩트 부옵. 엔진 몹은 서버가 만든다 */
export interface SlotInput {
    unitMasterId: number | null; name: string; attribute: string;
    stats: Stats; sets: number[]; art: Record<number, number>;
    relics: Array<[number, number, number]>;
    /** 방덱 선 스킬 칸(0부터) */
    opener?: number;
}
function toInput(s: Slot): SlotInput | null {
    const u = s.unit;
    if (!u?.found || !u.base || !s.stats) return null;
    const b = hasBuild(s.build) ? s.build! : null;
    const stats = b
        ? { hp: b.hp, atk: b.atk, def: b.def, spd: b.spd, critRate: b.cr, critDamage: b.cd, resistance: b.res, accuracy: b.acc }
        : s.stats;
    return {
        unitMasterId: u.unitMasterId ?? null, name: u.name, attribute: u.attribute, stats, sets: b ? b.sets : s.sets, art: b ? s.art || {} : {}, relics: b ? s.relics || [] : [],
        ...(s.opener !== undefined && s.opener !== null ? { opener: s.opener } : {})
    };
}

export default function SimPage({ isMobile, monsterMapping }: { isMobile: boolean; monsterMapping: any }) {
    const [atk, setAtk] = useState<Slot[]>([emptySlot(), emptySlot(), emptySlot()]);
    const [def, setDef] = useState<Slot[]>([emptySlot(), emptySlot(), emptySlot()]);
    // 칸에 몹이 들어오면(직접 고르기·전투 불러오기) 그 몹들의 스킬 아이콘을 미리 받는다 — 시뮬레이션·직접 플레이 재생 때 바로 뜨게
    const unitKey = [...atk, ...def].map((s) => s.unit?.unitMasterId ?? '').join(',');
    useEffect(() => { preloadSkillIcons([...atk, ...def].map((s) => s.unit)); }, [unitKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const [picking, setPicking] = useState<{ side: 'atk' | 'def'; idx: number } | null>(null);
    const [editing, setEditing] = useState<{ side: 'atk' | 'def'; idx: number } | null>(null);
    /**
     * 고른 몹 — 그 몹의 낀 룬(능력치·룬·아티)을 몹 줄 아래에 그린다. 처음은 맨 왼쪽(공덱 리더).
     * 몹 그림을 누르면 고르고, 이미 고른 몹(또는 빈 칸)을 누르면 몹 바꾸기 (사용자 2026-10-20 "룬설정 버튼도 없애고 선택된 몹이 표시되게")
     */
    const [sel, setSel] = useState<{ side: 'atk' | 'def'; idx: number }>({ side: 'atk', idx: 0 });
    const selSlot = (sel.side === 'atk' ? atk : def)[sel.idx];
    /** 고른 몹의 세팅 — 낀 룬 원본이 있으면 그걸로 셈한 것(룬 별을 그린다). 칸이 바뀔 때만 새로 만든다(BuildRunes 는 build 가 바뀌면 고치던 것을 버린다) */
    const selBuild = useMemo(() => {
        if (!selSlot?.pick || !selSlot.stats) return null;
        const fromRaw = selSlot.raw ? buildFromReplayUnit(selSlot.raw) : null;
        const b = fromRaw || (hasBuild(selSlot.build) ? selSlot.build! : typicalBuild(selSlot));
        // 아티팩트 부옵 — 낀 원본이면 속성·타입 아티팩트를 그대로, 아니면 칸에 적어 둔 둘(없으면 합을 나눠서)
        const arts: ArtPair = selSlot.raw ? artsOfRaw(selSlot.raw) : selSlot.arts || splitArt(selSlot.art || {});
        return b ? { ...b, arts } : null;
    }, [selSlot]);
    const [err, setErr] = useState('');

    const [running, setRunning] = useState(false);
    const [summary, setSummary] = useState<{ win: number; turns: number; timeout: number; panel?: { win: number; hard: number; runs: number; slot?: boolean; samples?: 'train' | 'latest'; samplesKey?: string } } | null>(null);
    /** 내 룬으로 맞춘 덱을 다시 친 압도적 판정 — 'busy' 는 치는 중, 'error' 는 못 침. 시뮬레이션을 새로 치면 지운다 */
    const [myVerdict, setMyVerdict] = useState<DeckVerdict | 'busy' | 'error' | null>(null);
    const verdictReq = useRef(0);
    const [replay, setReplay] = useState<ReplayData | null>(null);
    /** 이긴 판·진 판 재생 — 골라서 본다 */
    const [replays, setReplays] = useState<{ win: ReplayData | null; lose: ReplayData | null; close?: ReplayData | null } | null>(null);
    /** 직접 조작 — 누른 순간의 엔진 몹을 고정해 둔다(덱을 고치면 닫는다) */
    const [manual, setManual] = useState<{ atk: SlotInput[]; def: SlotInput[] } | null>(null);
    /** 방덱 선 스킬(첫 스킬) 지정 창 — 게임의 [첫 스킬 정보] */
    const [openerOpen, setOpenerOpen] = useState(false);

    const Select = isMobile ? MonsterSelectMobile : MonsterSelectDesktop;

    /** 고른 몹 자료를 서버에서 받아 기본 능력치로 채운다 */
    const choose = async (side: 'atk' | 'def', idx: number, m: any) => {
        setPicking(null);
        setErr('');
        const set = side === 'atk' ? setAtk : setDef;
        const pick = { name: m.name, attribute: m.attribute as Element, image_path: m.image_path, displayName: m.displayName };
        set((cur) => cur.map((s, i) => (i === idx ? { ...s, pick, unit: null, stats: null, build: null, art: {}, arts: undefined, raw: undefined } : s)));
        // 몹을 손수 바꾸면 불러온 판이 아니다 — 어느 불러오기 문구도 남기지 않는다
        setLoaded(null);
        setTestLoaded(null);
        setLearned(null);
        if (side === 'atk') setFitFrom(null);
        try {
            const r = await fetch(`/api/sim/units?mobs=${encodeURIComponent(`${m.name}:${m.attribute}`)}`);
            const d = await r.json();
            const u: SimUnit | undefined = d?.units?.[0];
            if (!u?.found) throw new Error('이 몹의 능력치·스킬 자료가 없습니다');
            set((cur) => cur.map((s, i) => (i === idx && s.pick?.name === m.name ? { ...s, unit: u, stats: typicalStats(u) } : s)));
        } catch (e: any) {
            setErr(e.message || '몹 자료를 받지 못했습니다');
        }
        setSummary(null);
        setReplay(null); setReplays(null);
        setManual(null);
    };

    /** 방덱 선 스킬 지정 — 한 몹의 한 칸만(엔진 firstSkill 은 하나). 같은 칸을 다시 누르면 끈다 */
    const setOpener = (idx: number, slotNo: number | null) => {
        setDef((cur) => cur.map((s, i) => {
            if (i === idx) return { ...s, opener: s.opener === slotNo ? null : slotNo };
            return s.opener === undefined || s.opener === null ? s : { ...s, opener: null };
        }));
        setSummary(null); setReplay(null); setReplays(null); setManual(null);
    };

    const ready = atk.every((s) => s.stats) && def.every((s) => s.stats);

    /** 전투기록에서 불러오기 — 고른 전투의 양쪽 몹·룬·아티팩트를 칸에 채운다 */
    const [loader, setLoader] = useState(false);
    const [loaded, setLoaded] = useState<{ logId: number; result: string; note: string } | null>(null);
    /** 길드 전투 연구에서 불러오기 (게임 길드 건물 → 전투 연구, 사용자 2026-10-21) */
    const [testOpen, setTestOpen] = useState(false);
    const [testLoaded, setTestLoaded] = useState<{ title: string; note: string } | null>(null);
    // 학습 기록 — 고른 방덱·공덱으로 칸을 채운다 (SimMemory). 시뮬레이션은 단추로
    const [memoryOpen, setMemoryOpen] = useState(false);
    /** [방덱학습] — 학습 서버가 새로 찾은 방덱과 그 카운터 (사용자 2026-10-30 "방덱학습 버튼 따로만들어서"). 창은 학습 기록과 같다(SimMemory kind='defense') */
    const [defOpen, setDefOpen] = useState(false);
    /** 불러온 학습 기록 공덱 — deck·slots(AI 가상 룬 칸)는 [AI 룬작에 내 룬 맞추기](SimFitRunes)가 쓴다. at 은 다시 불러오면 맞춘 결과를 지우려고 */
    const [learned, setLearned] = useState<{ win: number; games: number; deck: MemoryDeck; slots: Slot[]; at: number; samplesKey?: string } | null>(null);
    /** [내 룬 자동할당] — 고른 공덱 몹 룬 상자의 [룬 변경] 왼쪽 단추가 SimFitRunes 를 부른다 */
    const fitRef = useRef<SimFitRunesHandle>(null);
    /**
     * [내 룬 자동할당]을 쓸 수 있는 공덱 — 학습 기록·전투기록으로 불러온 칸 셋(되돌릴 원래 룬) (사용자 2026-10-29 "전투기록에서도 마찬가지고").
     * 몹을 손수 바꾸면 지운다. back = 되돌리는 단추 글자(학습 기록은 AI 가상 룬, 전투기록은 그 전투의 룬)
     */
    const [fitFrom, setFitFrom] = useState<{ slots: Slot[]; at: number; back: string } | null>(null);
    /**
     * 공덱에 내 룬이 끼워져 있나 — 그때 단추는 [AI 룬](학습 기록 AI 가상 룬으로 되돌리기) (사용자 2026-10-29 "내 룬 자동할당이 이미 되어있으면 AI 룬으로 바꿔줘").
     * 다른 공덱을 불러오면(fitFrom.at) 풀린다
     */
    const [myRunesAt, setMyRunesAt] = useState<number | null>(null);
    const myRunes = !!fitFrom && myRunesAt === fitFrom.at;
    const [fitBusy, setFitBusy] = useState(false);
    /** [룬 최적화] — 고른 공덱 몹 룬 상자의 [룬 변경] 왼쪽 단추가 SimRuneTune 을 부른다 (사용자 2026-10-29) */
    const tuneRef = useRef<SimRuneTuneHandle>(null);
    const [tuneBusy, setTuneBusy] = useState(false);
    /** 룬 최적화를 누를 때의 myRunesAt — [되돌리기]하면 단추도 그때로 */
    const tunePrevMy = useRef<number | null>(null);
    /**
     * [공덱 학습] → [결과 보기] — 그 방덱(리더 자리 그대로·2·3번 순서 없이)의 방금 그 학습 기록을 찾아 1등 공덱으로 칸을 채운다 (사용자 2026-10-27).
     * 학습 기록 창은 합격 덱이 있는 기록을 앞세워 다른 기록을 보일 수 있지만 여기는 방금 학습한 것이어야 한다(합격 0 이라도 — 사용자 2026-11-05, utils/simRecords.ts).
     * 학습 기록 창에서 고른 것과 같은 길(loadLearned)이다.
     */
    const showTrainResult = async (defense: number[]) => {
        setErr('');
        try {
            const rec = justTrainedRecord(await fetchDefenseRecords(defense));
            if (!rec || !rec.decks?.length) { setErr('학습 기록에서 이 방덱의 결과를 찾지 못했습니다'); return; }
            await loadLearned(rec, rec.decks[0]);
        } catch {
            setErr('학습 기록을 받지 못했습니다');
        }
    };
    const loadLearned = async (rec: MemoryRecord, deck: MemoryDeck, defBuild = 0) => {
        setMemoryOpen(false);
        setDefOpen(false);
        setErr('');
        // 앞서 불러온 전투기록·전투 연구 문구는 지운다 — 남아서 학습기록 문구와 같이 떴다(사용자 2026-10-22)
        setLoaded(null);
        setTestLoaded(null);
        try {
            const ids = [...deck.units, ...rec.defense];
            const ur = await fetch(`/api/sim/units?ids=${ids.join(',')}`).then((r) => r.json());
            const units: SimUnit[] = ur?.units || [];
            if (units.length !== 6 || units.some((u) => !u?.found)) throw new Error('이 기록에 엔진 자료가 없는 몹이 있습니다');
            const atkSlots: Slot[] = deck.units.map((_, i) => {
                const u = units[i];
                const s = deck.stats[i] || {};
                const sets = deck.sets[i] || [];
                return {
                    pick: { name: u.name, attribute: u.attribute as Element }, unit: u,
                    stats: { hp: s.hp, atk: s.atk, def: s.def, spd: s.spd, critRate: s.cri_rate, critDamage: s.cri_dmg, resistance: s.res, accuracy: s.acc },
                    sets,
                    build: { sets, hp: s.hp, atk: s.atk, def: s.def, spd: s.spd, cr: s.cri_rate, cd: s.cri_dmg, res: s.res, acc: s.acc },
                    art: deck.art?.[i] || {},
                    relics: (deck.relics?.[i] || []).filter((r) => r.length === 3) as Array<[number, number, number]>,
                    raw: deck.raw?.[i]
                };
            });
            // 방덱 — 기록에 실전 룬작(defenseBuilds)이 있으면 고른 것, 없으면 실전 중앙값 능력치
            const db = rec.defenseBuilds?.[defBuild] || rec.defenseBuilds?.[0];
            const defSlots: Slot[] = rec.defense.map((_, i) => {
                const u = units[3 + i];
                const b = db?.units[i];
                if (!b) return { pick: { name: u.name, attribute: u.attribute as Element }, unit: u, stats: typicalStats(u), sets: DEFAULT_SETS, build: null, art: {} };
                const s = b.stats;
                return {
                    pick: { name: u.name, attribute: u.attribute as Element }, unit: u,
                    stats: { hp: s.hp, atk: s.atk, def: s.def, spd: s.spd, critRate: s.cri_rate, critDamage: s.cri_dmg, resistance: s.res, accuracy: s.acc },
                    sets: b.sets,
                    build: { sets: b.sets, hp: s.hp, atk: s.atk, def: s.def, spd: s.spd, cr: s.cri_rate, cd: s.cri_dmg, res: s.res, acc: s.acc },
                    art: b.arts ? mergeArts(b.arts) : b.art || {},
                    ...(b.arts ? { arts: b.arts } : {}),
                    relics: (b.relics || []).filter((r) => r.length === 3) as Array<[number, number, number]>,
                    raw: b.raw
                };
            });
            setAtk(atkSlots);
            setDef(defSlots);
            const at = Date.now();
            setLearned({ win: deck.win, games: deck.games, deck, slots: atkSlots, at, samplesKey: rec.samplesKey });
            setFitFrom({ slots: atkSlots, at, back: 'AI 룬' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // 불러오기만 한다 — 바로 시뮬레이션하지 않는다(사용자 2026-10-20). 지난 결과는 지우고 맨 왼쪽 몹의 룬을 보인다
            setSummary(null);
            setReplay(null); setReplays(null);
            setManual(null);
            setSel({ side: 'atk', idx: 0 });
        } catch (e: any) {
            setErr(e.message || '학습 기록을 불러오지 못했습니다');
        }
    };
    const loadBattle = async (b: BattleRow, test?: { url: string; title: string }) => {
        setLoader(false);
        setTestOpen(false);
        setTestLoaded(null);
        setErr('');
        setFitFrom(null);
        setSummary(null);
        setReplay(null); setReplays(null);
        setManual(null);
        try {
            const team = [...b.attackTeam, ...b.defenseTeam];
            const [ur, br] = await Promise.all([
                fetch(`/api/sim/units?ids=${team.map((u) => u.unitMasterId).join(',')}`).then((r) => r.json()),
                fetch(test ? test.url : `/api/siege-battles/build/${b.ref || b.logId}`, { credentials: 'include' }).then((r) => r.json())
            ]);
            const units: SimUnit[] = ur?.units || [];
            if (units.length !== team.length || units.some((u) => !u?.found)) throw new Error('이 전투에 엔진 자료가 없는 몹이 있습니다');
            const notes: string[] = [];
            const fill = (side: 'attack' | 'defense', members: BattleRow['attackTeam'], offset: number): Slot[] => {
                const saved: any[] = br?.found && br[side] ? [...(br[side].units || [])] : [];
                if (!saved.length) notes.push(`${side === 'attack' ? '공덱' : '방덱'} 룬은 볼 수 없어 실전 중앙값으로 채웠습니다`);
                // 방덱 선 스킬 — 리플레이 defense_skill_option {pos_id, option:[칸-1, …]} 의 pos_id 몹(저장된 방덱 몹의 pos)에 첫 칸
                const dso = side === 'defense' ? br?.defenseSkillOption : null;
                const openerId = dso && Array.isArray(dso.option) && dso.option.length && Number(dso.option[0]) >= 0
                    ? Number(saved.find((x) => Number(x.pos) === Number(dso.pos_id))?.unitMasterId) || null : null;
                let openerUsed = false;
                return members.map((m, i) => {
                    const u = units[offset + i];
                    const at = saved.findIndex((x) => Number(x.unitMasterId) === Number(m.unitMasterId));
                    const su = at >= 0 ? saved.splice(at, 1)[0] : null;
                    const raw = su ? rawOf(su, i + 1) : null;
                    const build = raw ? buildFromReplayUnit(raw) : null;
                    return {
                        pick: { name: m.name, attribute: m.attribute as Element },
                        unit: u, stats: typicalStats(u), sets: DEFAULT_SETS,
                        build, art: raw ? artOf(raw) : {}, raw: raw || undefined,
                        relics: raw ? (raw.relics || []).map((r: any) => (r?.sec_effect || []).slice(0, 3).map(Number) as [number, number, number]).filter((r: number[]) => r.length === 3) : [],
                        ...(openerId && !openerUsed && Number(m.unitMasterId) === openerId ? ((openerUsed = true), { opener: Number(dso.option[0]) }) : {})
                    };
                });
            };
            const atkSlots = fill('attack', b.attackTeam, 0);
            setAtk(atkSlots);
            setFitFrom({ slots: atkSlots, at: Date.now(), back: '원래 룬' });
            setDef(fill('defense', b.defenseTeam, 3));
            if (test) { setLoaded(null); setTestLoaded({ title: test.title, note: notes.join(' · ') }); }
            else setLoaded({ logId: b.logId, result: b.result, note: notes.join(' · ') });
        } catch (e: any) {
            setErr(e.message || '전투기록을 불러오지 못했습니다');
        }
    };
    // 전투기록 창의 [시뮬레이션] 으로 왔으면 그 전투를 바로 불러온다(utils/simHandoff)
    useEffect(() => {
        const b = takeSimBattle();
        if (b) loadBattle(b);
        // 전적검색의 학습 결과 줄로 왔으면 그 방덱·공덱으로 칸을 채운다
        const l = takeSimLearned();
        if (l) loadLearned(l.rec, l.deck);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /** 서버에서 RUNS 판 + 재생 한 판 */
    const run = () => runWith(atk, def);
    const runWith = async (atkSlots: Slot[], defSlots: Slot[]) => {
        const A = atkSlots.map(toInput), D = defSlots.map(toInput);
        if (A.some((x) => !x) || D.some((x) => !x)) return;
        setRunning(true);
        setSummary(null);
        setReplay(null); setReplays(null);
        setManual(null);
        setErr('');
        const req = ++verdictReq.current;
        setMyVerdict(null);
        try {
            const r = await fetch('/api/sim/run', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ atk: A, def: D })
            });
            const d = await r.json();
            if (!d?.success) throw new Error(d?.error || '시뮬레이션에 실패했습니다');
            setSummary(d.summary);
            // 처음엔 [승리] 판을 골라 둔다(사용자 2026-10-22) — 서버의 대표 판(d.replay)은 세 단추 어느 것과도 같지 않아 아무것도 안 골라져 보였다
            const rs = d.replays || null;
            setReplay(rs?.win || rs?.close || rs?.lose || d.replay);
            setReplays(rs);
            // 내 룬으로 맞춘 덱 — 학습 기록 판정은 가상 룬 기준이라 같은 묶음으로 다시 판정한다. 시뮬 결과를 기다리게 하지 않게 따로 친다
            if (myRunes) {
                setMyVerdict('busy');
                fetch('/api/sim/verdict', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ atk: A, def: D }) })
                    .then((x) => x.json())
                    .then((v) => { if (req === verdictReq.current) setMyVerdict(v?.success && v.verdict ? v.verdict : 'error'); })
                    .catch(() => { if (req === verdictReq.current) setMyVerdict('error'); });
            }
        } catch (e: any) {
            setErr(e.message || '시뮬레이션에 실패했습니다');
        }
        setRunning(false);
    };

    // 공덱 유전 학습 판(SimEvolve)은 사이트에서 뺐다 (사용자 2026-10-21 "공덱 유전학습쪽 없애") — 공덱은 [학습 기록]으로 불러온다

    const panel: React.CSSProperties = {
        background: 'rgba(255,255,255,0.96)', borderRadius: 20, boxShadow: '0 10px 32px rgba(0,0,0,0.12)',
        padding: isMobile ? '10px 8px' : '20px 22px', width: '100%', boxSizing: 'border-box', marginBottom: isMobile ? 8 : 18
    };

    const editSlot = editing ? (editing.side === 'atk' ? atk : def)[editing.idx] : null;
    /**
     * 모바일 몹 칸 한 변 — 공덱 셋 · VS · 방덱 셋이 **한 줄에** 들어가게 화면 폭으로 정한다(사용자 2026-10-22, 412px 폭이면 45).
     * 바깥 여백(바탕 6·흰 판 8·금테 판 6+테 3, 양쪽) 50 · VS 26 · 칸 사이 틈 24 를 빼고 여섯으로 나눈 뒤 칸 둘레 몫(6)을 뺀다
     */
    const BOX = boxColors(getTheme() === 'dark');
    const mobSize = isMobile ? Math.max(34, Math.min(58, Math.floor((window.innerWidth - 50 - 26 - 24) / 6) - 6)) : 76;

    // 뒤로 가기: 몹 고르기·세팅 고치기·전투 불러오기 창을 닫는다
    useBackHandler(!!picking, () => setPicking(null));
    useBackHandler(!!editing && !!editSlot?.stats, () => setEditing(null));
    useBackHandler(loader, () => setLoader(false));
    useBackHandler(testOpen, () => setTestOpen(false));
    useBackHandler(memoryOpen, () => setMemoryOpen(false));
    useBackHandler(defOpen, () => setDefOpen(false));

    return (
        <div style={{
            width: '100%', minHeight: 'calc(100vh - 60px)', boxSizing: 'border-box',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: isMobile ? '8px 6px' : '24px 20px'
        }}>
            <div style={{ maxWidth: 980, margin: '0 auto' }}>
                <div style={panel}>
                    <h2 style={{
                        margin: '0 0 4px', textAlign: 'center', fontSize: isMobile ? 18 : 30, fontWeight: 900, letterSpacing: -0.5,
                        background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)', WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent', backgroundClip: 'text'
                    }}>시뮬레이션</h2>
                    <p style={{ margin: isMobile ? '0 0 8px' : '0 0 14px', textAlign: 'center', fontSize: isMobile ? 11 : 13, color: '#888', fontWeight: 600 }}>
                        전투기록을 불러오거나 공덱·방덱과 룬을 설정해주세요.
                    </p>
                    {/* 불러오기 — 단추 셋을 통계 탭 고르개처럼 붙인 한 묶음으로 (사용자 2026-10-27 "다른 탭처럼 영역을 나누자" 초안 나) */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: isMobile ? 4 : 6 }}>
                        <div style={{ display: 'inline-flex', border: '1px solid rgba(102, 126, 234, 0.3)', borderRadius: 999, overflow: 'hidden' }}>
                            {([['전투기록', () => setLoader(true)], ['전투연구', () => setTestOpen(true)], ['학습기록', () => setMemoryOpen(true)], ['방덱학습', () => setDefOpen(true)]] as Array<[string, () => void]>).map(([label, open], i) => (
                                <button key={label} type="button" onClick={open} style={{
                                    border: 'none', borderLeft: i ? '1px solid rgba(102, 126, 234, 0.2)' : 'none', background: '#f5f6ff', color: '#667eea',
                                    fontSize: isMobile ? 12 : 13, fontWeight: 800, padding: isMobile ? '6px 14px' : '7px 20px', cursor: 'pointer'
                                }}>{label}</button>
                            ))}
                        </div>
                    </div>
                    {learned && (
                        <div style={{ textAlign: 'center', fontSize: isMobile ? 11 : 12, fontWeight: 700, color: '#555', marginBottom: isMobile ? 8 : 10 }}>
                            학습 기록의 공덱을 불러왔습니다 · 학습 때 확정 승률 <b style={{ color: '#764ba2' }}>{(learned.win * 100).toFixed(1)}%</b> ({learned.games}판)
                            {learned.deck.verdict && <> · 가상 룬 판정(칸 룬작 없는 묶음) {verdictText(learned.deck.verdict)}{isOverwhelming(learned.deck.verdict) && <> <VerdictBadge /></>}</>}
                        </div>
                    )}
                    {testLoaded && (
                        <div style={{ textAlign: 'center', fontSize: isMobile ? 11 : 12, fontWeight: 700, color: '#555', marginBottom: isMobile ? 8 : 10 }}>
                            전투 연구 <b style={{ color: '#1976d2' }}>{testLoaded.title || '(제목 없음)'}</b> 의 덱·룬을 불러왔습니다
                            {testLoaded.note && <div style={{ color: '#e65100', marginTop: 2 }}>{testLoaded.note}</div>}
                        </div>
                    )}
                    {loaded && (
                        <div style={{ textAlign: 'center', fontSize: isMobile ? 11 : 12, fontWeight: 700, color: '#555', marginBottom: isMobile ? 8 : 10 }}>
                            전투기록 #{loaded.logId} 의 덱·룬을 불러왔습니다 · 실제 결과 <b style={{ color: loaded.result === 'win' ? '#1976d2' : '#d32f2f' }}>{loaded.result === 'win' ? '공덱 승' : '공덱 패'}</b>
                            {loaded.note && <div style={{ color: '#e65100', marginTop: 2 }}>{loaded.note}</div>}
                        </div>
                    )}

                </div>

                {/* 덱 판 — 전투 판 · 고른 몹 룬 · 시뮬레이션·직접 플레이 (머리 판과 나눔, 통계 탭처럼 판마다 따로) */}
                <div style={panel}>
                    {/*
                      전투 판 — 리더 스킬 줄 · 공덱 셋 · VS · 방덱 셋 · [첫 스킬 정보].
                      예전 금테 나무 판(게임 화면 흉내)은 사이트 남색·보라 차림에서 혼자 튀어서 바꿨다 — 색은 boxColors (다크·밝은 따로).
                    */}
                    <div style={{
                        position: 'relative', marginBottom: 14, borderRadius: 16,
                        padding: isMobile ? '30px 6px 10px' : '40px 18px 16px',
                        ...BOX.panel
                    }}>
                        {/* 리더 스킬 (공덱 리더 = 왼쪽 첫 칸) */}
                        <div style={{
                            position: 'absolute', left: isMobile ? 12 : 18, top: isMobile ? 9 : 12,
                            fontSize: isMobile ? 11 : 14, fontWeight: 800, color: BOX.leader
                        }}>리더 스킬 : <span style={{ color: BOX.leaderVal }}>{leaderText(atk[0])}</span></div>
                        {/* 첫 스킬 정보 (방덱 선 스킬 지정) */}
                        <button type="button" onClick={() => setOpenerOpen(true)} title="방덱 선 스킬 지정"
                            style={{
                                position: 'absolute', right: isMobile ? 10 : 16, top: isMobile ? 7 : 9, display: 'flex', alignItems: 'center', gap: 5,
                                borderRadius: isMobile ? 7 : 9, ...BOX.firstBtn,
                                fontSize: isMobile ? 9.5 : 13, fontWeight: 800, padding: isMobile ? '3px 7px' : '6px 12px', cursor: 'pointer'
                            }}>
                            첫 스킬 정보
                        </button>
                        {/* 위 맞춤 — 몹 칸 아래 이름·세팅 줄 길이가 달라도 몹 그림 줄이 한 높이. VS 는 몹 그림 칸 높이만큼만 차지해 그 가운데에 (사용자 2026-10-20) */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexWrap: isMobile ? 'nowrap' : 'wrap', gap: isMobile ? 4 : 14 }}>
                            <div style={{ display: 'flex', gap: isMobile ? 3 : 12 }}>
                                {atk.map((sv, idx) => (
                                    <SlotBox key={idx} slot={sv} idx={idx} side="atk" isMobile={isMobile} colors={BOX} size={mobSize} monsterMapping={monsterMapping}
                                        selected={sel.side === 'atk' && sel.idx === idx} onSelect={() => setSel({ side: 'atk', idx })}
                                        onPick={() => setPicking({ side: 'atk', idx })} />
                                ))}
                            </div>
                            {/* VS */}
                            <div style={{ position: 'relative', alignSelf: 'flex-start', width: isMobile ? 26 : 84, height: mobSize, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{
                                    position: 'relative', fontSize: isMobile ? 18 : 40, fontWeight: 900, fontStyle: 'italic', letterSpacing: -1,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                                    paddingRight: 4
                                }}>VS</span>
                            </div>
                            <div style={{ display: 'flex', gap: isMobile ? 3 : 12 }}>
                                {def.map((sv, idx) => (
                                    <SlotBox key={idx} slot={sv} idx={idx} side="def" isMobile={isMobile} colors={BOX} size={mobSize} monsterMapping={monsterMapping}
                                        opener={sv.opener ?? null}
                                        selected={sel.side === 'def' && sel.idx === idx} onSelect={() => setSel({ side: 'def', idx })}
                                        onPick={() => setPicking({ side: 'def', idx })} />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* 룬 최적화 — 진행·결과 (사용자 2026-10-29). 끝나서 오르면 맞춘 내 룬을 공덱 칸에 끼운다 — 불러온 공덱이면 단추는 [AI 룬]/[원래 룬] */}
                    <SimRuneTune ref={tuneRef} slots={atk} isMobile={isMobile} onBusy={setTuneBusy}
                        payload={() => { const A = atk.map(toInput), D = def.map(toInput); return A.some((x) => !x) || D.some((x) => !x) ? null : { atk: A, def: D }; }}
                        onApply={(slots, tuned) => { setAtk(slots); setSummary(null); setReplay(null); setReplays(null); setManual(null); setMyRunesAt(tuned ? fitFrom?.at ?? null : tunePrevMy.current); }} />

                    {/* 공덱 룬작에 내 룬 맞추기 — 학습 기록·전투기록 공덱을 불러왔을 때 (사용자 2026-10-27 · 10-29). 지금 칸(atk) 능력치에 맞춘다 */}
                    {fitFrom && (
                        <SimFitRunes ref={fitRef} key={fitFrom.at} slots={atk} isMobile={isMobile} onBusy={setFitBusy}
                            onApply={(slots) => { setAtk(slots); setSummary(null); setReplay(null); setReplays(null); setManual(null); setMyRunesAt(fitFrom.at); }} />
                    )}

                    {/*
                      고른 몹의 룬 — 몹 줄과 한 카드 안에 (사용자 2026-10-20 "룬 페이지의 영역을 합쳐" · "여기서 이제 룬 수정도 가능하게").
                      공략 [룬 정보] 탭과 같은 BuildRunes: 낀 룬 원본이 있으면 몹 창 + 룬 별 + 상세, [룬 변경]으로 고치면 원본(룬 별·상세)은 버리고 능력치 판만.
                      원본이 없는 몹은 직접 적은 능력치(없으면 실전 중앙값)를 몹 창으로.
                    */}
                    {(() => {
                        const s = selSlot;
                        if (!s?.pick || !selBuild) return null;
                        return (
                            <div style={{ marginBottom: 14 }}>
                                <BuildRunes key={`${sel.side}-${sel.idx}-${s.pick.name}-${s.pick.attribute}`} build={selBuild} mob={s.pick} mapping={monsterMapping} isMobile={isMobile} applyLabel="적용" withArt
                                    extraAction={sel.side === 'atk'
                                        ? [
                                            // 룬 최적화 — 이 공덱·방덱 그대로 승률이 가장 높은 룬 목표를 찾는다
                                            { label: '룬 최적화', onClick: () => { tunePrevMy.current = myRunesAt; tuneRef.current?.start(); }, busy: tuneBusy, busyLabel: '최적화 중…' },
                                            ...(fitFrom ? [myRunes
                                                // AI 룬 / 원래 룬 — 불러온 공덱 칸(학습 기록 AI 가상 룬 · 전투기록 룬)으로 되돌린다
                                                ? { label: fitFrom.back, onClick: () => { setAtk(fitFrom.slots); setSummary(null); setReplay(null); setReplays(null); setManual(null); setMyRunesAt(null); }, busy: false }
                                                : { label: '내 룬 자동할당', onClick: () => fitRef.current?.fit(), busy: fitBusy }] : [])
                                        ]
                                        : undefined}
                                    onSave={(b) => {
                                        // 비우면 실전 중앙값으로. 고치면 낀 룬 원본은 버린다(룬 별·상세가 사라진다)
                                        (sel.side === 'atk' ? setAtk : setDef)((cur) => cur.map((x, i) => (i === sel.idx ? { ...x, build: b ? { ...b, raw: undefined, arts: undefined } : null, art: b ? mergeArts(b.arts || [[], []]) : x.art, arts: b ? b.arts : x.arts, raw: undefined } : x)));
                                        setSummary(null);
                                        setReplay(null); setReplays(null);
                                        setManual(null);
                                    }} />
                            </div>
                        );
                    })()}

                    {err && <div style={{ color: '#d32f2f', fontSize: 13, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>{err}</div>}
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', rowGap: 6 }}>
                        <button
                            type="button" onClick={run} disabled={!ready || running}
                            style={{
                                border: 'none', borderRadius: 12, padding: isMobile ? '9px 22px' : '13px 44px', fontSize: isMobile ? 14 : 16, fontWeight: 900,
                                color: '#fff', cursor: ready && !running ? 'pointer' : 'not-allowed',
                                background: ready ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)',
                                opacity: ready && !running ? 1 : 0.7, boxShadow: ready ? '0 4px 16px rgba(102,126,234,0.4)' : 'none'
                            }}
                        >{running ? '싸우는 중…' : `시뮬레이션 (${RUNS}번)`}</button>
                        <button
                            type="button" disabled={!ready || running}
                            onClick={() => {
                                const A = atk.map(toInput), D = def.map(toInput);
                                if (A.some((x) => !x) || D.some((x) => !x)) return;
                                setManual({ atk: A as SlotInput[], def: D as SlotInput[] });
                            }}
                            style={{
                                marginLeft: isMobile ? 8 : 10, border: '2px solid #667eea', borderRadius: 12, padding: isMobile ? '7px 14px' : '11px 30px',
                                fontSize: isMobile ? 14 : 16, fontWeight: 900, color: '#667eea', background: '#fff',
                                cursor: ready && !running ? 'pointer' : 'not-allowed', opacity: ready && !running ? 1 : 0.6
                            }}
                        >직접 플레이</button>
                    </div>
                </div>

                {(() => {
                    if (!summary) return null;
                    // 게임에 턴 제한은 없다 — 엔진이 100턴에서 끊는 것은 학습용 장치라 사이트에는 그 얘기를 안 적는다(사용자 2026-10-22)
                    const win: number | null = summary.win;
                    const body = (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: isMobile ? 12 : 22, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: isMobile ? 13 : 14, color: '#888', fontWeight: 700 }}>공덱 승률</span>
                            <span style={{ fontSize: isMobile ? 28 : 44, fontWeight: 900, color: win == null ? '#999' : win >= 0.5 ? '#1976d2' : '#d32f2f' }}>{win == null ? '—' : `${(win * 100).toFixed(1)}%`}</span>
                            <span style={{ fontSize: isMobile ? 12 : 13, color: '#888', fontWeight: 700 }}>
                                {RUNS}번 · 평균 {Math.round(summary.turns)}턴
                            </span>
                        </div>
                        {/* 위 승률은 칸의 방덱 룬작 하나 상대, 학습 기록 승률은 묶음 평균이라 벌어질 수 있다 (사용자 2026-11-04 "학습 방덱 묶음 승률도 같이") */}
                        {summary.panel && (
                            <div title={`학습과 같은 방덱 묶음(실전 표본·가상 룬작·선턴작${summary.panel.slot ? '·칸 룬작 50%' : ''}, 100팀 1000판) 상대 승률입니다. 위 승률은 칸에 넣은 방덱 룬작 하나만 상대한 값입니다. 학습 기록의 판정 전체 승률은 칸 룬작 없는 1000팀 묶음이라 이 숫자와 다릅니다. 실전 표본은 ${summary.panel.samples === 'train' ? '이 방덱을 학습할 때 쓴 것' : '최신 것'}입니다.`} style={{ textAlign: 'center', marginTop: 2, fontSize: isMobile ? 12 : 13, color: '#666', fontWeight: 700 }}>
                                학습 방덱 묶음{summary.panel.slot && ' (칸 룬작 50% 포함)'} <b style={{ color: '#1976d2' }}>{(summary.panel.win * 100).toFixed(1)}%</b> · 어려운 50% <b style={{ color: '#1976d2' }}>{(summary.panel.hard * 100).toFixed(1)}%</b>
                                {learned?.samplesKey && summary.panel.samplesKey && learned.samplesKey !== summary.panel.samplesKey && (
                                    <div style={{ fontSize: isMobile ? 11 : 12, color: '#e65100' }}>불러온 기록은 다른 시점의 실전 표본으로 학습해 이 묶음과 숫자가 다를 수 있습니다</div>
                                )}
                            </div>
                        )}
                        {myVerdict && (
                            <div title="내 룬으로 맞춘 덱을 학습 기록 판정과 같은 묶음(칸 룬작 없는 1000팀 10,000판 + 실제 스펙 1000판)으로 다시 친 값입니다." style={{ textAlign: 'center', marginTop: 2, fontSize: isMobile ? 12 : 13, color: '#666', fontWeight: 700 }}>
                                {myVerdict === 'busy' ? '내 룬 판정 치는 중…'
                                    : myVerdict === 'error' ? '내 룬 판정을 치지 못했습니다'
                                    : <>내 룬 판정 {verdictText(myVerdict)} {isOverwhelming(myVerdict) ? <VerdictBadge mine /> : <span style={{ color: '#e65100' }}>기준 미달</span>}</>}
                            </div>
                        )}
                        {/* 판 보기 단추는 승률 판 안에 ('보기 :' 글자는 뺐다 — 사용자 2026-10-22) */}
                        {/*
                          셋을 둥근 상자 하나로 묶고 고른 칸만 그 판 색으로 채운다 — 고르지 않은 단추도 색 테두리라
                          다 골라진 것처럼 보였다(사용자 2026-10-22 C안, 화면 안 탭과 같은 모양)
                        */}
                        {replays && (replays.win || replays.lose) && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: isMobile ? 8 : 12 }}>
                        <div style={{ display: 'inline-flex', gap: 3, padding: 3, borderRadius: 12, border: '1.5px solid rgba(102, 126, 234, 0.3)', background: 'rgba(102, 126, 234, 0.06)' }}>
                            {([['win', '승리', '#1976d2'], ['close', '겨우 승리', '#f57c00'], ['lose', '패배', '#d32f2f']] as const).map(([k, name, color]) => {
                                const r = replays[k] || null;
                                const on = !!r && replay === r;
                                return (
                                    <button key={k} disabled={!r} onClick={() => r && setReplay(r)} title={r ? `${r.turns}턴` : '없음'}
                                        style={{
                                            border: 'none', borderRadius: 9, padding: isMobile ? '6px 12px' : '8px 22px', whiteSpace: 'nowrap',
                                            fontSize: isMobile ? 13 : 15, fontWeight: 900, color: on ? '#fff' : '#8a8fa3', background: on ? color : 'transparent',
                                            cursor: r ? 'pointer' : 'not-allowed', opacity: r ? 1 : 0.4
                                        }}
                                    >{name}</button>
                                );
                            })}
                        </div>
                        </div>
                        )}
                    </>
                    );
                    // 한 판 재생이 있으면 승률은 그 판 맨 위에 넣는다 — 두 상자로 나뉘어 있던 것을 합쳤다(사용자 2026-10-22)
                    if (replay?.frames) {
                        return <Replay key={replay === replays?.lose ? 'lose' : replay === replays?.close ? 'close' : 'win'} head={body} result={replay} atk={atk} def={def} isMobile={isMobile} monsterMapping={monsterMapping} panel={panel} />;
                    }
                    return <div style={panel}>{body}</div>;
                })()}

                {manual && (
                    <SimManual atk={manual.atk} def={manual.def} slots={[...atk, ...def]} monsterMapping={monsterMapping} panel={panel} />
                )}

                {!summary && replay?.frames && (
                    <Replay key={replay === replays?.lose ? 'lose' : replay === replays?.close ? 'close' : 'win'} result={replay} atk={atk} def={def} isMobile={isMobile} monsterMapping={monsterMapping} panel={panel} />
                )}

                {/* 공덱 학습 판 — 지금 방덱으로 학습 서버에서 학습 · 진행 · 취소 (사용자 2026-10-26 버튼, 2026-10-27 판으로 나눔 · 맨 밑으로) */}
                <div style={panel}>
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', rowGap: 6 }}>
                        <SimTrain defense={def.map((s) => s.unit?.unitMasterId)} slots={def.map(toInput)} set={def.map((s) => hasBuild(s.build) || !!s.raw)} monsterMapping={monsterMapping} isMobile={isMobile} onResult={showTrainResult} />
                    </div>
                </div>
            </div>

            {picking && (
                <Select
                    open
                    monsterMapping={monsterMapping}
                    onClose={() => setPicking(null)}
                    onSelect={(m: any) => choose(picking.side, picking.idx, m)}
                />
            )}

            {editing && editSlot?.stats && (
                <BuildEditor
                    open mob={editSlot.pick} mapping={monsterMapping}
                    build={hasBuild(editSlot.build) ? editSlot.build : typicalBuild(editSlot)}
                    onClose={() => setEditing(null)}
                    onSave={(b) => {
                        // 지우면 기본 능력치(실전 중앙값)로. 손으로 고쳐도 불러온 아티팩트 부옵은 그대로 둔다
                        (editing.side === 'atk' ? setAtk : setDef)((cur) => cur.map((s, i) => (i === editing.idx ? { ...s, build: b, raw: undefined } : s)));
                        setSummary(null);
                        setReplay(null); setReplays(null);
                        setManual(null);
                    }}
                />
            )}

            {loader && <BattlePicker monsterMapping={monsterMapping} onClose={() => setLoader(false)} onPick={(b) => { setLearned(null); loadBattle(b); }} />}
            {testOpen && <GuildTestPicker monsterMapping={monsterMapping} onClose={() => setTestOpen(false)} onPick={(b, title) => { setLearned(null); loadBattle(b, { url: `/api/guild-test/${b.logId}`, title }); }} />}
            {memoryOpen && <SimMemory monsterMapping={monsterMapping} onClose={() => setMemoryOpen(false)} onPick={loadLearned} />}
            {defOpen && <SimMemory kind="defense" monsterMapping={monsterMapping} onClose={() => setDefOpen(false)} onPick={loadLearned} />}

            {openerOpen && (
                <FirstSkillModal
                    def={def} monsterMapping={monsterMapping} isMobile={isMobile}
                    onPick={setOpener} onClose={() => setOpenerOpen(false)}
                />
            )}
        </div>
    );
}

const LEAD_STAT: Record<string, string> = {
    'Attack Power': '공격력', 'HP': '체력', 'Defense': '방어력', 'Attack Speed': '공격 속도',
    'Critical Rate': '치명 확률', 'Critical DMG': '치명 피해', 'Resistance': '저항', 'Accuracy': '명중'
};
const ELEM_KO: Record<string, string> = { Water: '물', Fire: '불', Wind: '풍', Light: '빛', Dark: '암' };

/** 리더 스킬 한 줄 (공덱 리더 몹) — 없으면 '버프 없음' (게임 표기) */
function leaderText(slot: Slot | undefined): string {
    const l = slot?.pick ? slot.unit?.leader : null;
    if (!l || !Number(l.amount)) return '버프 없음';
    const stat = LEAD_STAT[l.attribute] || l.attribute;
    const area = l.area && l.area !== 'General' && l.area !== 'Element' ? ({ Guild: '길드전 ', Arena: '아레나 ', Dungeon: '던전 ' } as Record<string, string>)[l.area] || '' : '';
    const elem = l.element ? `${ELEM_KO[l.element] || l.element} 속성 ` : '';
    return `${area}${elem}${stat} +${l.amount}%`;
}

/**
 * **첫 스킬 지정** — 게임의 [첫 스킬 정보] 창. 방덱 몹마다 스킬을 보여 주고, 하나를 눌러 **선 스킬**로 지정한다.
 * 엔진은 선 스킬을 방덱 한 몹에만 둔다(`firstSkill`) — 다른 몹의 지정을 누르면 그쪽으로 옮긴다. 같은 스킬을 다시 누르면 끈다. 패시브는 고를 수 없다.
 *
 * 차림은 사이트 판(흰 판·보라 강조 — 프로필 창과 같은 결, 다크모드는 utils/theme 가 어둡게) — 예전 금테 나무 판(게임 화면 흉내)을 바꿨다
 * (사용자 2026-10-28 초안 가안: 짜임은 그대로, 고른 스킬은 보라 테두리 + "첫 턴", 패시브는 흐리게, 빈 칸 ✕ 상자는 빼고 줄 오른쪽에 [지정 끄기] / 기본(AI)).
 */
function FirstSkillModal({ def, monsterMapping, isMobile, onPick, onClose }: {
    def: Slot[]; monsterMapping: any; isMobile: boolean; onPick: (idx: number, slot: number | null) => void; onClose: () => void;
}) {
    const box = isMobile ? 46 : 56;
    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 2200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
            background: 'rgba(15, 20, 35, 0.45)', backdropFilter: 'blur(4px)'
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 'min(560px, 96vw)', maxHeight: '88vh', overflowY: 'auto', borderRadius: 20, boxSizing: 'border-box',
                padding: isMobile ? '16px 12px 14px' : '20px 20px 18px',
                background: '#ffffff', border: '1px solid #e8ecf3', boxShadow: '0 18px 50px rgba(30, 40, 80, 0.25)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', marginBottom: 14 }}>
                    <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: '#222' }}>첫 스킬 지정</span>
                    <button onClick={onClose} aria-label="닫기" style={{
                        position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', width: 30, height: 30, borderRadius: 9,
                        border: 'none', background: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontSize: 18, fontWeight: 900, cursor: 'pointer', lineHeight: 1
                    }}>×</button>
                </div>
                {def.every((s) => !s.pick) ? (
                    <div style={{ textAlign: 'center', color: '#98a2b3', fontWeight: 700, fontSize: 13, padding: '26px 0' }}>방덱 몹을 먼저 골라 주세요.</div>
                ) : def.map((s, idx) => {
                    if (!s.pick) return null;
                    const img = getMonsterImgPath(monsterMapping, s.pick);
                    const skills = (s.unit?.skills || []).slice().sort((a, b) => a.slot - b.slot);
                    const has = s.opener !== undefined && s.opener !== null;
                    return (
                        <div key={idx} style={{
                            display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, marginBottom: 8,
                            padding: isMobile ? '8px' : '10px 12px', borderRadius: 14,
                            background: has ? 'rgba(102, 126, 234, 0.08)' : '#f7f8fc', border: `1px solid ${has ? 'rgba(102, 126, 234, 0.45)' : '#e8ecf3'}`
                        }}>
                            {img?.imgPath && <MonsterImage imgPath={img.imgPath} alt={img.displayName} style={{ width: box, height: box, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '2px solid #c9ccf5' }} />}
                            <div style={{ display: 'flex', gap: isMobile ? 5 : 8, flexWrap: 'wrap', paddingTop: 6 }}>
                                {skills.map((k) => {
                                    const on = s.opener === k.slot - 1;
                                    return (
                                        <button key={k.slot} type="button" disabled={k.passive}
                                            onClick={() => onPick(idx, k.slot - 1)}
                                            title={k.passive ? `${k.name} (패시브 — 선 스킬 불가)` : on ? `${k.name} — 다시 누르면 지정을 끕니다` : k.name}
                                            style={{
                                                position: 'relative', width: box - 6, height: box - 6, borderRadius: 10, padding: 0, background: '#eef0fa',
                                                border: on ? '2px solid #667eea' : '2px solid #e1e4f5',
                                                boxShadow: on ? '0 0 0 3px rgba(102, 126, 234, 0.3)' : 'none',
                                                cursor: k.passive ? 'default' : 'pointer', opacity: k.passive ? 0.35 : 1
                                            }}>
                                            <img src={skillIconUrl(k.id)} alt={k.name} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                                            {on && (
                                                <span style={{
                                                    position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap',
                                                    fontSize: 9.5, fontWeight: 900, color: '#fff', background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: 6, padding: '1px 5px'
                                                }}>첫 턴</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            {/* 줄 오른쪽 — 지정한 몹은 [지정 끄기], 아니면 엔진 AI 가 고른다 */}
                            <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
                                {has ? (
                                    <button type="button" onClick={() => onPick(idx, s.opener!)} style={{
                                        border: '1px solid rgba(102, 126, 234, 0.45)', background: '#fff', color: '#667eea', borderRadius: 999,
                                        padding: isMobile ? '3px 8px' : '4px 11px', fontSize: isMobile ? 10.5 : 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap'
                                    }}>지정 끄기</button>
                                ) : <span style={{ fontSize: isMobile ? 10.5 : 12, fontWeight: 700, color: '#98a2b3', whiteSpace: 'nowrap' }}>기본(AI)</span>}
                            </div>
                        </div>
                    );
                })}
                <div style={{ textAlign: 'center', color: '#667085', fontSize: isMobile ? 11 : 12, fontWeight: 600, margin: '6px 0 14px', lineHeight: 1.5 }}>
                    방덱 한 마리의 스킬을 누르면 그 몹의 첫 턴에 그 스킬을 씁니다 · 패시브는 고를 수 없습니다
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button onClick={onClose} style={{
                        border: 'none', borderRadius: 12, padding: isMobile ? '10px 44px' : '11px 56px', fontSize: isMobile ? 14 : 15, fontWeight: 800,
                        color: '#fff', background: 'linear-gradient(135deg, #667eea, #764ba2)', cursor: 'pointer'
                    }}>확인</button>
                </div>
            </div>
        </div>
    );
}

/** 몹 한 칸 — 비었으면 +, 골랐으면 이미지·이름·[능력치]. `opener` 는 방덱 선 스킬 칸(0부터) — 몹 위에 뱃지 */
function SlotBox({ slot, idx, side, isMobile, size, monsterMapping, onPick, onSelect, selected, opener, colors: C }: {
    slot: Slot; idx: number; side: 'atk' | 'def'; isMobile: boolean; size: number; monsterMapping: any; colors: ReturnType<typeof boxColors>;
    onPick: () => void; onSelect: () => void; selected: boolean; opener?: number | null;
}) {
    // 좁은 칸(모바일)은 둘레 몫을 줄이고 글자도 작게 — 이름·세팅 줄은 칸 폭에서 말줄임
    const colW = isMobile ? size + 6 : size + 10;
    const img = slot.pick ? getMonsterImgPath(monsterMapping, slot.pick) : null;
    const openerSkill = (opener !== undefined && opener !== null && slot.unit?.skills) ? slot.unit.skills.find((k) => k.slot === opener + 1) : null;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 2 : 4, width: colW, minWidth: 0 }}>
            <div
                // 빈 칸·이미 고른 몹 = 몹 바꾸기, 아니면 고르기(낀 룬을 아래에)
                onClick={!slot.pick || selected ? onPick : onSelect}
                title={!slot.pick ? (side === 'atk' ? '공덱 몹 고르기' : '방덱 몹 고르기') : selected ? '눌러서 몹 바꾸기' : '눌러서 낀 룬 보기'}
                style={{
                    width: size, height: size, borderRadius: isMobile ? 8 : 10, position: 'relative', cursor: 'pointer', boxSizing: 'border-box',
                    // 빈 칸은 점선, 몹이 들면 실선 · 리더 칸도 같은 모양('Leader' 글자로만 가른다) — 색은 boxColors
                    background: C.slotBg,
                    border: `${isMobile ? 1.5 : 2}px ${slot.pick ? 'solid' : 'dashed'} ${selected && slot.pick ? C.slotSel : slot.pick ? C.slotBorder : C.slotEmptyBorder}`,
                    boxShadow: selected && slot.pick ? `0 0 0 2px ${C.slotSel}, 0 0 14px rgba(139,147,255,0.6)` : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
                }}
            >
                {slot.pick && img?.imgPath ? (
                    <MonsterImage imgPath={img.imgPath} alt={img.displayName} style={{ width: size - 6, height: size - 6, objectFit: 'cover', borderRadius: 6 }} />
                ) : (
                    <span style={{ fontSize: idx === 0 ? (isMobile ? 10 : 16) : (isMobile ? 18 : 26), color: idx === 0 ? C.leaderMark : C.plus, fontWeight: 900 }}>
                        {idx === 0 ? 'Leader' : '+'}
                    </span>
                )}
                {slot.pick && idx === 0 && (
                    <span style={{
                        position: 'absolute', left: 3, top: 3, fontSize: 9, fontWeight: 900, color: '#fff', background: 'rgba(0,0,0,0.6)',
                        borderRadius: 4, padding: '1px 4px', letterSpacing: 0.3
                    }}>L</span>
                )}
                {/* 오른쪽 위 비우기(×) 단추는 두지 않는다 (사용자 2026-10-20) — 몹은 그림을 눌러 바꾼다 */}
                {openerSkill && (
                    <img src={skillIconUrl(openerSkill.id)} alt={openerSkill.name} title={`선 스킬: ${openerSkill.name}`} draggable={false}
                        style={{ position: 'absolute', left: -6, bottom: -6, width: size * 0.42, height: size * 0.42, borderRadius: 7, border: '2px solid #f6d77a', boxShadow: '0 1px 4px rgba(0,0,0,0.6)' }} />
                )}
            </div>
            {/* 빈 칸 아래 '공덱'·'방덱' 글자는 두지 않는다 (사용자 2026-10-20) */}
            {slot.pick && (
                <span style={{ fontSize: isMobile ? 10 : 11, fontWeight: 800, color: C.name, maxWidth: isMobile ? colW : size + 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {img?.displayName || slot.pick.name}
                </span>
            )}
            {slot.pick && (slot.stats ? (
                <>
                    {isMobile ? (
                        // 좁으면 속도와 세트를 두 줄로 — 한 줄이면 옆 칸 글자와 겹쳤다
                        <>
                            <span style={{ fontSize: 9, fontWeight: 700, color: hasBuild(slot.build) ? C.stat : C.statMuted, whiteSpace: 'nowrap' }}>
                                {hasBuild(slot.build) ? `속 ${slot.build!.spd}` : `중앙 속 ${slot.stats.spd}`}
                            </span>
                            {hasBuild(slot.build) && (
                                <span style={{ fontSize: 8.5, fontWeight: 700, color: C.sets, maxWidth: colW, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {setsText(slot.build!.sets) || '세트 없음'}
                                </span>
                            )}
                        </>
                    ) : (
                        <span style={{ fontSize: 10, fontWeight: 700, color: hasBuild(slot.build) ? C.stat : C.statMuted, whiteSpace: 'nowrap' }}>
                            {hasBuild(slot.build) ? `속 ${slot.build!.spd} · ${setsText(slot.build!.sets) || '세트 없음'}` : `중앙값 · 속 ${slot.stats.spd}`}
                        </span>
                    )}
                </>
            ) : <span style={{ fontSize: 11, color: '#aaa' }}>…</span>)}
        </div>
    );
}

/** 한 판 재생 — 장면(`frames`)을 한 장씩 넘긴다 */
function Replay({ head, result, atk, def, isMobile, monsterMapping, panel }: {
    /** 판 맨 위에 얹는 것 (승률·판 고르기 단추) */
    head?: React.ReactNode;
    result: ReplayData; atk: Slot[]; def: Slot[]; isMobile: boolean; monsterMapping: any; panel: React.CSSProperties;
}) {
    const frames = result.frames!;
    const [at, setAt] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [speed, setSpeed] = useState(1);
    const timer = useRef<number | null>(null);

    // 새 판이 오면 처음부터
    useEffect(() => { setAt(0); setPlaying(true); }, [result]);

    const slots = [...atk, ...def];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const nameOf = useMemo(() => nameMap(slots, monsterMapping), [atk, def, monsterMapping]);

    const cur = frames[at];
    const prevEnd = at > 0 ? frames[at - 1].logEnd : 0;
    const lines: BattleLog[] = result.log.slice(prevEnd, cur.logEnd);
    const last = at === frames.length - 1;
    /** 때린 몹이 대상 앞까지 달려가 타수만큼 치고 오기 · 맞은 몹은 타마다 흔들리고 깎인다 — 협공·반격은 차례로 (`simShared`) */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const strikes = useMemo(() => strikesOf(lines, slots, cur.actor), [at, result]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const passives = useMemo(() => passivesOf(lines, slots), [at, result]);
    // 타격이 길면(다타·협공·반격) 그만큼 더 보여 준다
    const extra = Math.max(0, strikes.total * 1000 + 450 - 900);

    useEffect(() => {
        if (!playing) return;
        if (at >= frames.length - 1) { setPlaying(false); return; }
        timer.current = window.setTimeout(() => setAt((x) => Math.min(frames.length - 1, x + 1)), (900 + extra) / speed);
        return () => { if (timer.current) window.clearTimeout(timer.current); };
    }, [playing, at, speed, frames.length, extra]);

    /** 이번 장면에 몹마다 띄울 글자·쓴 스킬 (`simShared`) */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const floats = useMemo(() => floatsOf(lines, slots), [at, result]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const used = useMemo(() => usedSkillsOf(lines, slots, cur.actor), [at, result]);


    const btn: React.CSSProperties = {
        border: '1px solid #d0d7e2', borderRadius: 8, background: '#fff', color: '#546e7a', fontWeight: 800,
        fontSize: isMobile ? 12 : 13, padding: isMobile ? '5px 8px' : '7px 14px', cursor: 'pointer'
    };

    return (
        <div style={panel}>
            {head && (
                <div style={{ paddingBottom: isMobile ? 10 : 14, marginBottom: isMobile ? 10 : 14, borderBottom: '1px solid rgba(102, 126, 234, 0.15)' }}>{head}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: isMobile ? 13 : 15, fontWeight: 900, color: '#333' }}>한 판 재생</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: last && !result.timeout ? (result.win ? '#1976d2' : '#d32f2f') : '#888' }}>
                    {last && !result.timeout ? (result.win ? '공덱 승' : '방덱 승') : `${cur.turn}턴`}
                </span>
            </div>
            {/* 게임 전투 화면처럼 — 경기장 배경, 상대 방덱 위 · 우리 공덱 아래 */}
            <BattleField
                slots={slots} units={cur.units} monsterMapping={monsterMapping} acting={cur.actor}
                floats={floats} floatKey={at} used={used} strikes={strikes} prevUnits={at > 0 ? frames[at - 1].units : undefined} rate={speed}
                passives={passives} height={isMobile ? 380 : 540}
            />

            {/* 이번 행동 */}
            <div style={{ marginTop: isMobile ? 6 : 10, minHeight: isMobile ? 56 : 80, maxHeight: isMobile ? 110 : 140, overflowY: 'auto', background: '#f7f8fc', borderRadius: 10, padding: isMobile ? '6px 8px' : '8px 10px' }}>
                {at === 0 && <div style={{ fontSize: 12, color: '#999', fontWeight: 600 }}>싸움 시작</div>}
                {lines.map((l, k) => (
                    <div key={k} style={{ fontSize: isMobile ? 10.5 : 12, color: '#444', fontWeight: 600, lineHeight: 1.55 }}>
                        <b>{nameOf(l.actor)}</b>{' '}
                        {(() => {
                            const k = slots.findIndex((x) => x.pick && `${x.pick.name}(${x.pick.attribute})` === l.actor && skillIdOf(x, l.skill) !== null);
                            const id = k >= 0 ? skillIdOf(slots[k], l.skill) : null;
                            return <>
                                {id ? <img src={skillIconUrl(id)} alt="" width={16} height={16} style={{ verticalAlign: '-3px', borderRadius: 3, marginRight: 3 }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} /> : null}
                                {skillLabel(k >= 0 ? slots[k] : slots[slotIndexOf(slots, l.actor)], l.skill, l.note)}
                            </>;
                        })()}
                        {l.target && <> → <b>{l.target === '(아군)' ? '아군' : nameOf(l.target)}</b></>}
                        {l.damage ? <span style={{ color: '#e53935', fontWeight: 900 }}> {l.crit && <CritIcon size={15} />}{l.damage.toLocaleString()}{l.glancing ? ' 빗맞음' : ''}</span> : null}
                        {noteLabel(l.skill, l.note) && <span style={{ color: '#999' }}> · {noteLabel(l.skill, l.note)}</span>}
                    </div>
                ))}
            </div>

            {/* 조작 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 6, marginTop: isMobile ? 6 : 10, flexWrap: 'wrap' }}>
                <button type="button" style={btn} onClick={() => { setPlaying(false); setAt((x) => Math.max(0, x - 1)); }}>◀</button>
                <button type="button" style={{ ...btn, minWidth: 54 }} onClick={() => { if (last) setAt(0); setPlaying(!playing || last); }}>{playing ? '멈춤' : last ? '처음부터' : '재생'}</button>
                <button type="button" style={btn} onClick={() => { setPlaying(false); setAt((x) => Math.min(frames.length - 1, x + 1)); }}>▶</button>
                {[1, 2, 4].map((v) => (
                    <button key={v} type="button" onClick={() => setSpeed(v)} style={{ ...btn, background: speed === v ? '#667eea' : '#fff', color: speed === v ? '#fff' : '#546e7a' }}>{v}×</button>
                ))}
                <input
                    type="range" min={0} max={frames.length - 1} value={at}
                    onChange={(e) => { setPlaying(false); setAt(Number(e.target.value)); }}
                    style={{ flex: 1, minWidth: isMobile ? 90 : 120, accentColor: '#667eea' }}
                />
            </div>
        </div>
    );
}

/** 불러올 전투기록 한 줄 (`GET /api/sim/battles`) */
/** 불러오기 창 두 칸의 최대 높이 — 창(최대 86vh)에서 제목·닫기 몫(100px)을 뺀다 */
const PANE_MAX = 'calc(86vh - 100px)';

interface BattleRow {
    /** 전투 열쇠(서버 utils/battleRef) — 없으면 옛 서버 */
    ref?: string;
    logId: number; battledAt: string; result: string; wizardId?: number; wizardName: string; oppWizardName?: string; oppGuildName?: string; baseNumber?: number;
    attackTeam: Array<{ unitMasterId: number; name: string; attribute: string }>;
    defenseTeam: Array<{ unitMasterId: number; name: string; attribute: string }>;
}

/**
 * **전투기록 고르기** — 리플레이 룬을 받아 둔 공격 기록을 **한 줄씩, 최신순**(사용자 2026-10-22: 방덱별로 합치지 말고).
 * 위 고르기: [전체] · [내 기록](내 길드원 번호 = 로그인한 계정의 wizardId) · [승리] · [패배] — 하나만 고른다(사용자 2026-10-22).
 * 줄: 친 사람 얼굴·이름·날짜 · 공덱 셋 · 승/패 · 방덱 셋 · 방덱 주인 · 불러오기.
 */
function BattlePicker({ monsterMapping, onClose, onPick }: { monsterMapping: any; onClose: () => void; onPick: (b: BattleRow) => void }) {
    const [rows, setRows] = useState<BattleRow[] | null>(null);
    const [err, setErr] = useState('');
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    const [me, setMe] = useState<number | null>(null);
    const [view, setView] = useState<'all' | 'mine' | 'win' | 'lose'>('all');
    const narrow = typeof window !== 'undefined' && window.innerWidth < 720;
    useEffect(() => {
        fetch('/api/sim/battles?limit=200').then((r) => r.json())
            .then((d) => { if (d?.success) setRows(d.battles); else setErr(d?.error || '목록을 받지 못했습니다'); })
            .catch(() => setErr('목록을 받지 못했습니다'));
        fetch('/api/siege-battles/avatars').then((r) => r.json())
            .then((d) => { if (d?.success) setAvatars(d.avatars || {}); })
            .catch(() => { /* 얼굴이 없으면 기본 그림 */ });
        fetch('/api/auth/me', { credentials: 'include' }).then((r) => r.json())
            .then((d) => { const w = Number(d?.wizardId); if (w > 0) setMe(w); })
            .catch(() => { /* 길드원 번호가 없으면 [내 기록] 을 못 고른다 */ });
    }, []);
    const shown = useMemo(() => (rows || []).filter((b) =>
        view === 'all' ? true : view === 'mine' ? me != null && Number(b.wizardId) === me : b.result === view
    ), [rows, view, me]);
    // 몹이 셋보다 적은 판도 줄이 세로로 맞게 셋 몫 폭을 잡는다
    const team = (t: BattleRow['attackTeam'], size = 34) => (
        <div style={{ display: 'flex', gap: 3, flexShrink: 0, width: size * 3 + 6 }}>
            {t.map((m, i) => {
                const img = getMonsterImgPath(monsterMapping, m);
                return img.imgPath
                    ? <MonsterImage key={i} imgPath={img.imgPath} alt={img.displayName} style={{ width: size, height: size, borderRadius: 7, objectFit: 'contain' }} />
                    : <span key={i} style={{ width: size, fontSize: 9 }}>{m.name}</span>;
            })}
        </div>
    );
    const chip = (on: boolean, label: string, onClick: () => void, disabled = false) => (
        <button type="button" onClick={onClick} disabled={disabled} title={disabled ? '로그인한 계정에 길드원 번호가 없습니다' : undefined} style={{
            border: on ? 'none' : '1px solid rgba(102, 126, 234, 0.35)', borderRadius: 999, padding: narrow ? '5px 12px' : '6px 14px',
            fontSize: narrow ? 12 : 13, fontWeight: 800, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
            background: on ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff', color: on ? '#fff' : '#667eea'
        }}>{label}</button>
    );
    const size = narrow ? 26 : 34;
    /** 전투기록 탭처럼 — 몹 셋을 테두리 상자에 넣고 위에 WIN/LOSE 표 (이긴 쪽 파랑, 진 쪽 빨강) */
    const box = (t: BattleRow['attackTeam'], won: boolean) => (
        <div style={{
            position: 'relative', flexShrink: 0, borderRadius: 10, padding: narrow ? '9px 4px 4px' : '11px 6px 5px',
            border: `${narrow ? 1.5 : 2}px solid ${won ? '#2196F3' : '#f44336'}`, background: won ? 'rgba(33, 150, 243, 0.05)' : 'rgba(244, 67, 54, 0.05)'
        }}>
            <span style={{
                position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)', background: won ? '#2196F3' : '#f44336', color: '#fff',
                padding: narrow ? '0 6px' : '1px 9px', borderRadius: 7, fontSize: narrow ? 8.5 : 10, fontWeight: 800, letterSpacing: 0.4, lineHeight: '14px'
            }}>{won ? 'WIN' : 'LOSE'}</span>
            {team(t, size)}
        </div>
    );
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                // 높이를 고정한다 — 고르기에 따라 판 수가 달라져 창이 줄었다 커졌다 했다(사용자 2026-10-22)
                background: '#fff', borderRadius: 18, padding: '14px 14px 10px', width: 'min(760px, 96vw)', boxSizing: 'border-box',
                height: 'min(86vh, 760px)', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}>
                <div style={{ marginBottom: 10 }}><span style={{ fontSize: 18, fontWeight: 900, letterSpacing: -0.3, background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>전투기록 불러오기</span></div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {chip(view === 'all', '전체', () => setView('all'))}
                    {chip(view === 'mine', '내 기록', () => setView('mine'), me == null)}
                    {chip(view === 'win', '승리', () => setView('win'))}
                    {chip(view === 'lose', '패배', () => setView('lose'))}
                    {rows && <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 12, fontWeight: 700, color: '#999' }}>{shown.length}판</span>}
                </div>
                {/* 목록 칸은 늘 남은 높이를 다 차지한다 — 판이 적어도 창 크기가 그대로 */}
                <div style={{ overflowY: 'auto', flex: '1 1 0', minHeight: 0, border: '1px solid rgba(102, 126, 234, 0.18)', borderRadius: 12, padding: '2px 10px', background: '#f8faff' }}>
                        {err && <div style={{ color: '#d32f2f', fontSize: 13, fontWeight: 700, padding: '10px 2px' }}>{err}</div>}
                        {!rows && !err && <div style={{ color: '#999', fontSize: 13, padding: '10px 2px' }}>불러오는 중…</div>}
                        {rows && !shown.length && <div style={{ color: '#999', fontSize: 13, padding: '10px 2px' }}>{rows.length ? '맞는 전투기록이 없습니다' : '룬을 받아 둔 전투기록이 없습니다'}</div>}
                        {shown.map((b) => (
                            <div key={b.ref || b.logId} onClick={() => onPick(b)} style={{
                                display: 'flex', alignItems: 'center', gap: narrow ? 5 : 8, padding: '9px 2px', borderBottom: '1px solid rgba(102, 126, 234, 0.1)', cursor: 'pointer'
                            }}>
                                {/* 친 사람(얼굴은 사이트 다른 곳처럼 네모)이 맨 왼쪽, 폭을 정해 몹 줄이 세로로 맞는다 */}
                                <img loading="lazy" src={avatars[b.wizardName] || '/images/default-profile.png'} alt="" title={b.wizardName}
                                    onError={(e) => { const img = e.currentTarget; if (!img.src.endsWith('/images/default-profile.png')) img.src = '/images/default-profile.png'; }}
                                    style={{ width: narrow ? 22 : 28, height: narrow ? 22 : 28, borderRadius: 6, objectFit: 'cover', background: '#e3f0ff', flexShrink: 0 }} />
                                {/* 좁은 화면은 이름·날짜 칸을 빼 상자 둘과 상대가 한 줄에 들어가게 */}
                                {!narrow && (
                                    <div style={{ width: 64, flexShrink: 0, textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#666', lineHeight: 1.35, minWidth: 0 }}>
                                        <div style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.wizardName}</div>
                                        <div>{new Date(b.battledAt).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</div>
                                    </div>
                                )}
                                {box(b.attackTeam, b.result === 'win')}
                                <span style={{ fontSize: narrow ? 11 : 13, fontWeight: 900, color: '#667eea', flexShrink: 0, letterSpacing: 0.5 }}>VS</span>
                                {box(b.defenseTeam, b.result !== 'win')}
                                {/* 상대 — 방덱 바로 오른쪽에 길드 그림(전투기록 탭과 같은 길드 프로필, guildImg) + 닉네임 (사용자 2026-10-22) */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, flexShrink: 1 }}>
                                    {b.oppGuildName && (
                                        <img loading="lazy" src={guildImg(b.oppGuildName)} alt={b.oppGuildName} title={`${b.oppWizardName || ''} · ${b.oppGuildName}`}
                                            onError={(e) => { const img = e.currentTarget; if (!img.src.endsWith('/images/unknown2.png')) img.src = '/images/unknown2.png'; }}
                                            style={{ width: narrow ? 24 : 30, height: narrow ? 24 : 30, borderRadius: 7, objectFit: 'cover', background: '#eef0f8', flexShrink: 0 }} />
                                    )}
                                    {!narrow && <span style={{ fontSize: 11, fontWeight: 700, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{b.oppWizardName}</span>}
                                </div>
                                {/* 좁은 화면은 칸이 모자라 뺀다 — 줄 어디를 눌러도 불러온다 */}
                                {!narrow && <span style={{ marginLeft: 'auto', border: '1.5px solid #667eea', borderRadius: 8, color: '#4b5fd6', background: '#fff', fontSize: 11, fontWeight: 800, padding: '4px 8px', flexShrink: 0 }}>불러오기</span>}
                            </div>
                        ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <button type="button" onClick={onClose} style={{ border: 'none', borderRadius: 10, padding: '9px 16px', fontWeight: 800, background: '#eef1f5', color: '#555', cursor: 'pointer' }}>닫기</button>
                </div>
            </div>
        </div>
    );
}

/**
 * **길드 전투 연구 고르기** — 게임 전투 연구 창과 같은 짜임(사용자 2026-10-21): 왼쪽 방덱 목록(번호·올린 사람·방덱 셋·판 수),
 * 오른쪽 고른 방덱의 판 목록(친 사람·날짜, 공덱 셋 vs 방덱 셋 — 제목 줄·찾기 칸은 뺐다, 사용자 2026-10-21). 판을 고르면 그 판의 공덱·방덱·룬·아티·유물로 칸을 채운다
 * (`loadBattle` 을 같이 쓴다 — 룬 주소만 `/api/guild-test/:ref`). 좁은 화면은 방덱 목록 → 판 목록으로 넘어간다.
 */
function GuildTestPicker({ monsterMapping, onClose, onPick }: { monsterMapping: any; onClose: () => void; onPick: (b: BattleRow, title: string) => void }) {
    type Row = BattleRow & { title: string; comment: string; deckOwner: string; deckId: number; deckNumber: number | null };
    type Deck = { deckId: number; deckNumber: number | null; title: string; owner: string; defenseTeam: Row['defenseTeam']; reps: Row[] };
    const [rows, setRows] = useState<Row[] | null>(null);
    const [err, setErr] = useState('');
    /**
     * **[새로고침]** — 에이전트가 게임에서 길드 건물 → 전투 연구에 들어가 아직 없는 판을 열어 온다(사용자 2026-10-21).
     * 사이트는 PC 를 직접 못 부르니 서버에 요청을 남기고(`POST /api/guild-test/refresh`), 에이전트가 가져가 돌며 알리는
     * 진행 상황을 3초마다 본다. 끝나면 목록을 다시 받는다.
     */
    const [tick, setTick] = useState(0);
    const [job, setJob] = useState<{ state: string; message: string; opened: number; decks: number } | null>(null);
    const busy = job?.state === 'queued' || job?.state === 'running';
    const startRefresh = () => {
        fetch('/api/guild-test/refresh', { method: 'POST', credentials: 'include' }).then((r) => r.json())
            .then((d) => { if (d?.success) setJob(d.status); else setJob({ state: 'error', message: d?.error || '요청하지 못했습니다', opened: 0, decks: 0 }); })
            .catch(() => setJob({ state: 'error', message: '요청하지 못했습니다', opened: 0, decks: 0 }));
    };
    useEffect(() => {
        if (!busy) return;
        const t = window.setInterval(() => {
            fetch('/api/guild-test/refresh', { credentials: 'include' }).then((r) => r.json())
                .then((d) => {
                    if (!d?.success || !d.status) return;
                    setJob(d.status);
                    if (d.status.state === 'done' || d.status.state === 'error') setTick((n) => n + 1);   // 목록 다시
                })
                .catch(() => {});
        }, 3000);
        return () => window.clearInterval(t);
    }, [busy]);
    useEffect(() => {
        fetch('/api/guild-test', { credentials: 'include' }).then((r) => r.json())
            .then((d) => {
                if (!d?.success) { setErr(d?.error || '목록을 받지 못했습니다'); return; }
                setRows((d.items || []).map((x: any) => ({
                    logId: x.replayInfoRef, ref: String(x.replayInfoRef), battledAt: x.battledAt || x.capturedAt, result: '',
                    wizardName: x.attacker || '', attackTeam: x.attackTeam || [], defenseTeam: x.defenseTeam || [],
                    title: x.title || '', comment: x.comment || '', deckOwner: x.deckOwner || '',
                    deckId: Number(x.deckId) || 0, deckNumber: x.deckNumber ?? null
                })));
            })
            .catch(() => setErr('목록을 받지 못했습니다'));
    }, [tick]);
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    useEffect(() => {
        fetch('/api/siege-battles/avatars').then((r) => r.json())
            .then((d) => { if (d?.success) setAvatars(d.avatars || {}); })
            .catch(() => { /* 얼굴이 없으면 기본 그림 */ });
    }, []);
    /** 방덱별로 묶는다 — 방덱 번호가 큰(나중에 올린) 것이 위, 판은 최신이 위(게임 창과 같다) */
    const [pick, setPick] = useState<number | null>(null);
    const narrow = typeof window !== 'undefined' && window.innerWidth < 720;
    const decks = useMemo<Deck[]>(() => {
        if (!rows) return [];
        const by = new Map<number, Deck>();
        for (const b of rows) {
            const key = b.deckId || -Number(b.logId);
            const d = by.get(key) || { deckId: key, deckNumber: b.deckNumber, title: b.title, owner: b.deckOwner, defenseTeam: b.defenseTeam, reps: [] };
            d.reps.push(b);
            by.set(key, d);
        }
        const out = [...by.values()];
        out.forEach((d) => d.reps.sort((a, b) => new Date(b.battledAt).getTime() - new Date(a.battledAt).getTime()));
        return out.sort((a, b) => (b.deckNumber ?? -1) - (a.deckNumber ?? -1));
    }, [rows]);
    const sel = decks.find((d) => d.deckId === pick) || (narrow ? null : decks[0]) || null;
    const team = (t: BattleRow['attackTeam'], size = 34) => (
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
            {t.map((m, i) => {
                const img = getMonsterImgPath(monsterMapping, m);
                return img.imgPath
                    ? <MonsterImage key={i} imgPath={img.imgPath} alt={img.displayName} style={{ width: size, height: size, borderRadius: 7, objectFit: 'contain' }} />
                    : <span key={i} style={{ width: size, fontSize: 9 }}>{m.name}</span>;
            })}
        </div>
    );
    const deckList = (
        <div style={{ overflowY: 'auto', minHeight: 0, maxHeight: PANE_MAX, ...(narrow ? { flex: 1 } : { width: 186, flexShrink: 0, paddingRight: 4 }) }}>
            {decks.map((d) => {
                const on = !!sel && d.deckId === sel.deckId;
                return (
                    <div key={d.deckId} onClick={() => setPick(d.deckId)} style={{
                        border: on ? '2px solid #667eea' : '1px solid rgba(102, 126, 234, 0.18)', borderRadius: 12,
                        background: on ? 'linear-gradient(145deg, #f5f7ff 0%, #eceffe 100%)' : '#fff',
                        boxShadow: on ? '0 4px 14px rgba(102, 126, 234, 0.25)' : '0 1px 4px rgba(0, 0, 0, 0.05)',
                        padding: on ? '6px 8px' : '7px 9px', marginBottom: 7, cursor: 'pointer', textAlign: 'left'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, color: '#333' }}>
                            <span style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 11 }}>{d.deckNumber ?? '-'}</span>
                            <ProfileWithName profileImg={avatars[d.owner]} nickname={d.owner} size={20} fontSize={12} color="#333" borderRadius={5} style={{ flex: 1, minWidth: 0, gap: 5 }} />
                        </div>
                        {/* 기록 수는 몹 줄 오른쪽 — 윗줄은 닉네임이 다 보이게 */}
                        <div style={{ marginTop: 5, display: 'flex', alignItems: 'flex-end', gap: 4 }}>
                            {team(d.defenseTeam, 34)}
                            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, color: on ? '#667eea' : '#999', whiteSpace: 'nowrap' }}>기록 {d.reps.length}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
    const repList = sel && (
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, alignSelf: 'flex-start', maxHeight: PANE_MAX, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', border: '1px solid rgba(102, 126, 234, 0.18)', borderRadius: 12, padding: '8px 10px', background: '#f8faff' }}>
            {narrow && <button type="button" onClick={() => setPick(null)} style={{ alignSelf: 'flex-start', border: 'none', background: 'none', color: '#667eea', fontWeight: 800, fontSize: 12, cursor: 'pointer', padding: '2px 0 6px' }}>‹ 방덱 목록</button>}
            <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                {sel.reps.map((b) => (
                    <div key={b.ref} onClick={() => onPick(b, b.title)} style={{
                        display: 'flex', alignItems: 'center', gap: narrow ? 5 : 8, padding: '9px 2px', borderBottom: '1px solid rgba(102, 126, 234, 0.1)', cursor: 'pointer'
                    }}>
                        {/* 친 사람(얼굴은 사이트 다른 곳 — 방덱 탭 글쓴이처럼 네모)이 맨 왼쪽, 폭을 정해 몹 줄이 세로로 맞는다 */}
                        <img loading="lazy" src={avatars[b.wizardName] || '/images/default-profile.png'} alt=""
                            onError={(e) => { const img = e.currentTarget; if (!img.src.endsWith('/images/default-profile.png')) img.src = '/images/default-profile.png'; }}
                            style={{ width: narrow ? 22 : 28, height: narrow ? 22 : 28, borderRadius: 6, objectFit: 'cover', background: '#e3f0ff', flexShrink: 0 }} />
                        <div style={{ width: narrow ? 50 : 64, flexShrink: 0, textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#666', lineHeight: 1.35, minWidth: 0 }}>
                            <div style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.wizardName}</div>
                            <div>{new Date(b.battledAt).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</div>
                        </div>
                        {team(b.attackTeam, narrow ? 28 : 34)}
                        <span style={{ fontSize: 11, fontWeight: 900, color: '#764ba2' }}>vs</span>
                        {team(b.defenseTeam, narrow ? 28 : 34)}
                        {/* 좁은 화면은 칸이 모자라 뺀다 — 줄 어디를 눌러도 불러온다 */}
                        {!narrow && <span style={{ marginLeft: 'auto', border: '1.5px solid #667eea', borderRadius: 8, color: '#4b5fd6', background: '#fff', fontSize: 11, fontWeight: 800, padding: '4px 8px', flexShrink: 0 }}>불러오기</span>}
                    </div>
                ))}
            </div>
        </div>
    );
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                background: '#fff', borderRadius: 18, padding: '14px 14px 10px', width: 'min(700px, 96vw)', boxSizing: 'border-box',
                maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}><span style={{ fontSize: 18, fontWeight: 900, letterSpacing: -0.3, background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>전투연구 불러오기</span></div>
                    <button type="button" onClick={startRefresh} disabled={busy} style={{
                        border: 'none', borderRadius: 10, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontSize: 12, fontWeight: 800,
                        padding: '7px 14px', cursor: busy ? 'default' : 'pointer', boxShadow: '0 3px 10px rgba(102, 126, 234, 0.35)', opacity: busy ? 0.85 : 1,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
                    }}>
                        {/* 받는 동안은 글자 대신 도는 고리(App.css 의 spin) — 글자는 자리만 남겨 단추 폭이 그대로다(사용자 2026-10-21) */}
                        <span style={{ visibility: busy ? 'hidden' : 'visible' }}>새로고침</span>
                        {busy && <span role="status" aria-label="받는 중" style={{
                            position: 'absolute', width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff',
                            animation: 'spin 0.8s linear infinite', boxSizing: 'border-box'
                        }} />}
                    </button>
                </div>
                {/* 다 받았다는 말은 안 띄운다 — 목록이 새로 그려지면 된다. 못 받았을 때만 까닭을 적는다 */}
                {job?.state === 'error' && (
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: '#d32f2f' }}>못 받았습니다 — {job.message}</div>
                )}
                {err && <div style={{ color: '#d32f2f', fontSize: 13, fontWeight: 700 }}>{err}</div>}
                {!rows && !err && <div style={{ color: '#999', fontSize: 13 }}>불러오는 중…</div>}
                {rows && !decks.length && <div style={{ color: '#999', fontSize: 13 }}>아직 받은 판이 없습니다 — [새로고침] 을 누르면 에이전트가 게임에서 받아 옵니다</div>}
                {!!decks.length && (
                    <div style={{ display: 'flex', gap: 10, flex: '1 1 auto', minHeight: 0 }}>
                        {(!narrow || !sel) && deckList}
                        {repList}
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <button type="button" onClick={onClose} style={{ border: 'none', borderRadius: 10, padding: '9px 16px', fontWeight: 800, background: '#eef1f5', color: '#555', cursor: 'pointer' }}>닫기</button>
                </div>
            </div>
        </div>
    );
}
